import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, effect, input, model, output, signal } from '@angular/core';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { Subject } from 'rxjs';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';

import { AuthorizationService } from '../../../../../modules/fwcore/services/authorization.service';
import { ImpersonationService } from '../../../../../modules/fwcore/services/impersonation.service';
import { AdminService } from '../../../services/admin.service';

import { FormsModule } from '@angular/forms';
import { UserCompanySummary } from '../../../models/user-company';
import { RequestUserCompanySummary, ResponseUserCompanySummary } from '../../../models/messages/user-company-messages';

export const ctUserCompanyStorageKey = 'selectedCompanyId';

@Component({
  selector: 'app-user-company-droplist',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonsModule, DropDownListModule],
  templateUrl: './user-company-droplist.component.html',
  styleUrl: './user-company-droplist.component.scss'
})
export class UserCompanyDroplistComponent implements OnInit, OnDestroy {

  

  public controlWidth = input<number>(200);
  public filterUserGroup = input<string | null>(null);
  public companyId = model<string | null>(null);

  public heading = input<string | null>(null);
  public headingCssClass = input<string | null>(null);

  protected readonly STORAGE_KEY = ctUserCompanyStorageKey;

  /**
   * The list of available companies to display in the drop-down.
   */
  public dataItems = model<UserCompanySummary[]>([]);
  public selected = output<UserCompanySummary | null>();

  //----------------- State properties  ------------------

  protected filteredDataItems = signal<UserCompanySummary[]>([]);

  protected selectedItem = computed(() => {
    const id = this.companyId();
    const items = this.dataItems();

    if (!items || items.length === 0) return null;
    return id ? items.find(i => i.companyId === id) ?? null : null;
  });

  protected loading = signal<boolean>(false);
  protected destroy$ = new Subject<void>();
  protected debounceGetData = new Subject<void>();

  //------------------- Constructor  --------------------------
  constructor(
    public serviceAuth: AuthorizationService,
    protected adminService: AdminService,
    protected impersonationService: ImpersonationService
  ) {
    // Debounce the get data call
    this.debounceGetData.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(() => this.getData());

    // Re-resolve the company selection whenever impersonation starts or
    // stops - the previous selection belonged to a different user's context
    // and must not carry over to the newly active user. Skip the first
    // (initial) run so normal, non-impersonated persisted selections aren't
    // disturbed on every page load.
    let firstRun = true;
    effect(() => {
      const impersonating = this.impersonationService.impersonating();

      if (firstRun) {
        firstRun = false;
        return;
      }

      this.companyId.set(impersonating ? this.impersonationService.companyId() : null);
      if (!impersonating) {
        localStorage.removeItem(this.STORAGE_KEY);
      }
      this.debounceGetData.next();
    });
  }

  //-------------------- ng  ----------------------------------
  ngOnInit(): void {
    this.debounceGetData.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------- Control Processing -------------
  protected loaderShow(): void {
    this.loading.set(true);
  }

  protected loaderClose(): void {
    this.loading.set(false);
  }

  // ---------------- Event Processing -------------
  public onValueChange(value: UserCompanySummary | null): void {
    this.handleSelectionChange(value);
  }

  public onSelectionChange(value: UserCompanySummary | null): void {
    this.handleSelectionChange(value);
  }

  private handleSelectionChange(value: UserCompanySummary | null): void {
    
    const newId = value?.companyId ?? null;

    // Update the model (syncs with parent)
    this.companyId.set(newId);

    // Persist to localStorage - but never while impersonating, so a
    // manually-picked company for the impersonated session can't leak into
    // the admin's own future sessions.
    if (!this.impersonationService.impersonating()) {
      if (newId) {
        localStorage.setItem(this.STORAGE_KEY, newId);
      } else {
        localStorage.removeItem(this.STORAGE_KEY);
      }
    }

    // Emit the selection event
    this.selected.emit(value);
  }

  public onFilterChange(filter: string): void {
    const items: UserCompanySummary[] = this.dataItems() ?? [];
    const filterLower = (filter ?? '').toLowerCase();
    const filtered = items.filter(
      (item) => (item.companyName ?? '').toLowerCase().includes(filterLower)
    );
    this.filteredDataItems.set(filtered);
  }

  /**
   * Determines the initial selection based on stored value, isMain flag, or first item
   */
  protected determineInitialSelection(): string | null {
    const items = this.dataItems();

    // While impersonating, the impersonated user's own company is
    // authoritative - trust it even if it isn't present in the list the
    // admin's own permissions resolved (the list is scoped to the real
    // caller, not the impersonated target).
    if (this.impersonationService.impersonating()) {
      return this.impersonationService.companyId();
    }

    if (!items || items.length === 0) return null;

    // 1. Check if parent already provided a companyId
    const parentId = this.companyId();
    if (parentId && items.some(i => i.companyId === parentId)) {
      return parentId;
    }

    // 2. Check localStorage for previously selected company
    const storedId = localStorage.getItem(this.STORAGE_KEY);
    if (storedId && items.some(i => i.companyId === storedId)) {
      return storedId;
    }

    // 3. Find item where isMain === true
    const mainItem = items.find(i => i.isMain === true);
    if (mainItem) {
      return mainItem.companyId ?? null;
    }

    // 4. Default to first item
    return items[0]?.companyId ?? null;
  }

  // -------------------- API Processing --------------------------
  protected getData(): void {
    this.loaderShow();
    const filterByGroup = this.filterUserGroup();
    const req: RequestUserCompanySummary = {
      codeUserGroup: filterByGroup
    };

    this.adminService.getData$<RequestUserCompanySummary, ResponseUserCompanySummary>(
      req,
      this.adminService.userCompanySummaryEndpoint
    )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderClose())
      )
      .subscribe((resp) => {
        if (resp.success === true) {
          const items = resp.data ?? [];
          this.dataItems.set(items);
          this.filteredDataItems.set(items);

          // Determine and set initial selection
          const initialId = this.determineInitialSelection();
          if (initialId) {
            this.companyId.set(initialId); // selectedItem updates automatically via computed

            // Emit the computed selection
            this.selected.emit(this.selectedItem());

            // Store the selection - but never persist an impersonated user's
            // company, it must not leak into the admin's own future sessions.
            if (!this.impersonationService.impersonating()) {
              localStorage.setItem(this.STORAGE_KEY, initialId);
            }
          }
        }
      });
  }
}
